import React, { useState, useEffect, useRef } from "react";
import CustomSearch from "./CustomSearch";

const sizeClasses = {
  xs: "px-2 py-0",
  "xs-sm": "px-2.5 py-1",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  "xs-sm": "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const iconSizeClasses = {
  xs: "!text-[12px]",
  "xs-sm": "!text-[14px]",
  sm: "!text-[14px]",
  md: "!text-[14px]",
  lg: "!text-[15px]",
  xl: "!text-[15px]",
};

const CustomRadioSelect = ({
  options = [],
  value = null, // single selected value
  onChange = () => {},
  rounded = "rounded",
  disabled,
  size = "md",
  labelKey = "label",
  valueKey = "value",
  placeholder = "Select option...",
  noBorder = false,
  showSearch = false,
  shadow = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions =
    searchValue?.length > 0
      ? options.filter((opt) =>
          opt[labelKey].toLowerCase().includes(searchValue.toLowerCase())
        )
      : options;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false); // close after selection
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={toggleDropdown}
        className={`w-full border flex justify-between items-center focus:outline-none ${
          sizeClasses[size]
        } ${fontSizeClasses[size]} ${rounded} 
        ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
        ${
          disabled
            ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
            : "bg-white hover:border-primary focus:border-primary "
        } ${noBorder ? "border-0" : ""}`}
        type="button"
        disabled={disabled}
        ref={buttonRef}
      >
        <span
          className={`truncate ${!value ? "text-gray-400" : "text-gray-700"}`}
        >
          {value
            ? options.find((opt) => opt[valueKey] === value)?.[labelKey]
            : placeholder}
        </span>
        <span
          className={`material-icons transition-transform ${iconSizeClasses[size]} ml-1 flex-shrink-0`}
        >
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </button>

      {/* Dropdown Options */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            width: buttonRef.current?.offsetWidth,
          }}
          className="mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {showSearch && (
            <div className="mx-2 mt-2">
              <CustomSearch
                placeholder="Search"
                value={searchValue}
                onChange={(key, val) => setSearchValue(val)}
                size={size}
                rounded="rounded-full"
              />
              <hr className="mt-2 text-gray-300" />
            </div>
          )}

          <ul className="p-2 space-y-1">
            {filteredOptions.map((opt) => (
              <li
                key={opt[valueKey]}
                className="flex items-center p-2 rounded cursor-pointer hover:bg-primary-50"
                onClick={() => {
                  handleSelect(opt[valueKey]);
                  setSearchValue("");
                }}
              >
                <input
                  type="radio"
                  checked={value === opt[valueKey]}
                  readOnly
                  className="mr-2 cursor-pointer appearance-none h-4 w-4 border border-gray-400 rounded-full
              checked:bg-white
             relative
             before:content-[''] before:absolute before:inset-[2px] before:rounded-full before:bg-primary
             before:scale-0 checked:before:scale-100 before:transition-transform before:duration-200"
                />

                <span className={`flex-1 ${fontSizeClasses[size]}`}>
                  {opt[labelKey]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomRadioSelect;
